import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { markets } from "@/data/markets";
import { fadeUp, EASE } from "@/lib/motion";

const AFRICA_PATH =
  "M 267,52 L 296,55 L 308,72 L 320,86 L 328,90 L 334,106 L 358,121 L 382,125 L 390,142 L 403,153 L 416,152 L 426,155 L 439,163 L 455,176 L 470,182 L 482,198 L 486,215 L 496,220 L 502,234 L 504,251 L 511,259 L 520,254 L 529,258 L 533,272 L 527,287 L 526,297 L 519,307 L 517,318 L 510,324 L 498,332 L 484,336 L 470,327 L 458,321 L 452,327 L 440,329 L 431,333 L 423,336 L 416,344 L 412,354 L 400,362 L 397,378 L 384,377 L 373,382 L 366,392 L 355,401 L 340,402 L 329,395 L 319,388 L 306,381 L 292,375 L 280,373 L 267,360 L 258,342 L 250,327 L 241,306 L 233,289 L 229,269 L 227,251 L 216,236 L 208,215 L 206,196 L 209,179 L 220,158 L 226,149 L 227,133 L 236,117 L 247,101 L 248,86 L 261,73 L 267,62 Z";

function project([lon, lat]) {
  const lonMin = -18;
  const lonMax = 52;
  const latMin = -35;
  const latMax = 37;
  const xMin = 205;
  const xMax = 533;
  const yMin = 52;
  const yMax = 402;
  const x = xMin + ((lon - lonMin) / (lonMax - lonMin)) * (xMax - xMin);
  const y = yMin + ((latMax - lat) / (latMax - latMin)) * (yMax - yMin);
  return [x, y];
}

export default function AfricaMap({ compact = false }) {
  const [active, setActive] = useState("tanzania");
  const current = markets.find((m) => m.id === active);

  return (
    <section
      data-testid="africa-map"
      className="relative bg-[#0a1930] text-[#f4f0e6] overflow-hidden grain"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24 lg:py-40 relative z-10">
        {!compact && (
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-4">
              <span className="overline-tag text-[#f26522]">Focus markets</span>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-[clamp(2rem,4.6vw,4.5rem)] leading-[1.02] tracking-tight">
                Four markets.
                <span className="font-display-italic text-[#f26522]"> A focused approach.</span>
              </h2>
              <p className="mt-6 text-[#f4f0e6]/70 max-w-[560px]">
                Tanzania, Zambia, Zimbabwe and Botswana are Medicual's current focus
                markets.
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          <motion.div {...fadeUp} className="lg:col-span-7 relative">
            <div className="relative aspect-[340/380] w-full max-w-[640px]">
              <svg
                viewBox="185 40 360 380"
                className="w-full h-full"
                aria-label="Africa focus markets map"
                role="img"
              >
                {[80, 140, 200, 260, 320, 380].map((y) => (
                  <line
                    key={"h" + y}
                    x1="185"
                    x2="545"
                    y1={y}
                    y2={y}
                    stroke="rgba(244,240,230,0.06)"
                  />
                ))}
                {[240, 300, 360, 420, 480].map((x) => (
                  <line
                    key={"v" + x}
                    y1="40"
                    y2="420"
                    x1={x}
                    x2={x}
                    stroke="rgba(244,240,230,0.06)"
                  />
                ))}

                <path
                  d={AFRICA_PATH}
                  fill="rgba(244,240,230,0.06)"
                  stroke="rgba(244,240,230,0.5)"
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <path
                  d={AFRICA_PATH}
                  fill="none"
                  stroke="rgba(242,101,34,0.35)"
                  strokeWidth="0.6"
                  transform="translate(3 3)"
                  strokeLinejoin="round"
                />

                {markets.map((m) => {
                  const [x, y] = project(m.coord);
                  const isActive = active === m.id;
                  return (
                    <g
                      key={m.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`${m.name} — focus market`}
                      onClick={() => setActive(m.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setActive(m.id);
                        }
                      }}
                      data-testid={`map-country-${m.id}`}
                      className="cursor-pointer focus:outline-none"
                    >
                      {isActive && (
                        <circle
                          cx={x}
                          cy={y}
                          r="22"
                          fill="rgba(242,101,34,0.14)"
                        />
                      )}
                      <circle
                        cx={x}
                        cy={y}
                        r={isActive ? 6.5 : 4}
                        fill={isActive ? "#f26522" : "rgba(244,240,230,0.9)"}
                        stroke={isActive ? "#f4f0e6" : "rgba(10,25,48,0.7)"}
                        strokeWidth="1.2"
                        style={{ transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)" }}
                      />
                      <text
                        x={x + 10}
                        y={y + 3}
                        fill={isActive ? "#f4f0e6" : "rgba(244,240,230,0.65)"}
                        className="overline-tag"
                        style={{ fontSize: 8, letterSpacing: "0.18em" }}
                      >
                        {m.name.toUpperCase()}
                      </text>
                    </g>
                  );
                })}
              </svg>

              <div className="absolute top-0 left-0 overline-tag opacity-40">
                N 38°
              </div>
              <div className="absolute bottom-0 right-0 overline-tag opacity-40">
                S 37° · E 55°
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="border border-white/15 p-8 lg:p-10 bg-[#0f2340]/90 md:bg-[#0f2340]/60 md:backdrop-blur"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="overline-tag text-[#f26522]">
                      Focus market
                    </span>
                    <span className="overline-tag opacity-40">
                      {current.region}
                    </span>
                  </div>
                  <h3
                    className="font-display text-[clamp(2rem,4vw,3.75rem)] leading-[1] tracking-tight"
                    data-testid={`market-panel-title-${current.id}`}
                  >
                    {current.name}
                  </h3>
                  <p className="mt-8 text-base text-[#f4f0e6]/72 leading-relaxed max-w-[420px]">
                    One of four markets currently identified by Medicual as a focus
                    market.
                  </p>
                </motion.div>
              </AnimatePresence>

              <ul className="mt-8 lg:hidden divide-y divide-white/12 border-t border-white/12">
                {markets.map((m) => (
                  <li key={m.id}>
                    <button
                      data-testid={`map-list-${m.id}`}
                      onClick={() => setActive(m.id)}
                      className={`w-full flex items-center justify-between py-4 overline-tag ${
                        active === m.id
                          ? "text-[#f26522]"
                          : "text-[#f4f0e6]/70"
                      }`}
                    >
                      <span>{m.name}</span>
                      <span className="opacity-50">{m.region}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className="hidden md:block absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full bg-[#f26522] opacity-[0.06] blur-[130px]"
      />
    </section>
  );
}
